import type { TourEvent } from '@/features/events';
import { formatDayLabel, formatLongDate, formatWeekdayUpper } from '@/features/events';
import { Section } from '@/components/layout';
import { ProgramItem } from '@/components/ui';
import { Reveal } from '@/components/motion';
import styles from './ProgramaFunes.module.css';

const TITLE_ID = 'funes-programa';

/** Ornamento por bloque, en el orden del programa. Puro adorno, no dato. */
const BLOCK_ICONS = ['seed', 'lotus', 'heart', 'sun', 'star'] as const;

export interface ProgramaFunesProps {
  event: TourEvent;
}

/**
 * Programa de las dos jornadas en Alma Sana.
 *
 * El cliente lo mandó el 26-oct-2026 con su propio nombre, «Convivencia en Alma
 * Sana: Despertar & Activación Cuántica», que va de entradilla. Horas, títulos y
 * fases salen de `event.program`; aquí no se teclea ningún dato.
 */
export function ProgramaFunes({ event }: ProgramaFunesProps) {
  if (event.program.length === 0) return null;

  return (
    <Section background="alt" spacing="tight" labelledBy={TITLE_ID} className={styles.section}>
      <Reveal className={styles.head}>
        <h2 id={TITLE_ID} className={styles.title}>
          Programa
        </h2>
        <p className={styles.lead}>Convivencia en Alma Sana: Despertar &amp; Activación Cuántica</p>
      </Reveal>

      <div className={styles.days}>
        {event.program.map((day, dayIndex) => (
          <section key={day.day} className={styles.day} aria-labelledby={`funes-programa-dia-${day.day}`}>
            <h3 id={`funes-programa-dia-${day.day}`} className={styles.dayHead}>
              <span className={styles.dayLabel}>{formatDayLabel(day)}</span>
              <span className={styles.dayDate}>
                <time dateTime={day.date}>
                  {formatWeekdayUpper(day.date)} · {formatLongDate(day.date)}
                </time>
              </span>
              <span className={styles.dayTitle}>{day.title}</span>
            </h3>

            <ol className={styles.blocks} role="list">
              {day.blocks.map((block, blockIndex) => (
                <ProgramItem
                  key={block.id}
                  block={block}
                  icon={BLOCK_ICONS[(dayIndex * 2 + blockIndex) % BLOCK_ICONS.length] ?? 'star'}
                  className={styles.block}
                />
              ))}
            </ol>
          </section>
        ))}
      </div>
    </Section>
  );
}
